import React, { useMemo } from "react";
import {
  Box,
  Autocomplete,
  TextField,
  Button,
  Typography,
  useTheme,
} from "@mui/material";
import FileDownloadOutlinedIcon from "@mui/icons-material/FileDownloadOutlined";
import TrendingUpOutlinedIcon from "@mui/icons-material/TrendingUpOutlined";
import PositionsCalculationControlsV2 from "../../PerformanceDashboardV2/components/PositionsCalculationControlsV2";
import {
  v2CardBg,
  v2CardBorder,
  v2MutedSx,
  v2ControlFieldSx,
  v2AutocompleteSlotProps,
  filterStringOptions,
  filterFundOptions,
} from "../../PerformanceDashboardV2/v2Theme";
import {
  resolveFundAbbreviation,
  fundAbbreviationsMatch,
} from "../../Portfolio/utils/portfolioFundUtils";

const renderFundOption = (theme) => (props, option) => {
  const { key, ...optionProps } = props;
  return (
    <Box
      component="li"
      key={key}
      {...optionProps}
      sx={{ display: "block !important", minWidth: 0 }}
    >
      <Typography
        sx={{
          fontWeight: 700,
          fontSize: "0.9rem",
          overflow: "hidden",
          textOverflow: "ellipsis",
          whiteSpace: "nowrap",
        }}
      >
        {option.name || option.abbreviation}
      </Typography>
      {option.name &&
        option.abbreviation &&
        option.name !== option.abbreviation && (
          <Typography
            sx={{
              fontSize: "0.72rem",
              fontWeight: 600,
              ...v2MutedSx(theme),
            }}
          >
            {option.abbreviation}
          </Typography>
        )}
    </Box>
  );
};

const renderTextOption = (props, option) => {
  const { key, ...optionProps } = props;
  return (
    <Box
      component="li"
      key={key}
      {...optionProps}
      sx={{ display: "block !important", minWidth: 0 }}
    >
      <Typography
        sx={{
          fontWeight: 700,
          fontSize: "0.9rem",
          overflow: "hidden",
          textOverflow: "ellipsis",
          whiteSpace: "nowrap",
        }}
      >
        {option}
      </Typography>
    </Box>
  );
};

const ControlBar = ({
  fundVal,
  StrategeyVal,
  StrategeyTypename,
  fundOptions = [],
  strategyOptions = [],
  StrategyType = [],
  funds = [],
  isStrategist,
  disableStrategyButton,
  StrategyName,
  handleFundChange,
  handleStrategyChange,
  handleSideChange,
  onExport,
  onTopMovers,
}) => {
  const theme = useTheme();

  const strategyDisabled = isStrategist
    ? false
    : disableStrategyButton ||
      fundVal === "" ||
      !StrategyName ||
      StrategyName?.length < 1;

  const fundSelectOptions = useMemo(() => {
    const allOption = { abbreviation: "ALL", name: "All Funds" };
    const fromFunds = (funds || []).map((fund) => ({
      abbreviation: fund.abbreviation,
      name: fund.name || fund.abbreviation,
    }));
    if (fromFunds.length > 0) return [allOption, ...fromFunds];

    return (fundOptions || []).map((abbr) => ({
      abbreviation: abbr,
      name: abbr === "ALL" ? "All Funds" : abbr,
    }));
  }, [funds, fundOptions]);

  const selectedFund = useMemo(() => {
    const resolved = resolveFundAbbreviation(funds, fundVal);
    return (
      fundSelectOptions.find((f) => fundAbbreviationsMatch(f.abbreviation, resolved)) ||
      fundSelectOptions[0] ||
      null
    );
  }, [fundSelectOptions, fundVal, funds]);

  return (
    <Box
      sx={{
        width: "100%",
        display: "flex",
        alignItems: "center",
        gap: 1.5,
        flexWrap: "wrap",
        py: 1.25,
        px: 1.5,
        borderRadius: 2,
        border: "1px solid",
        borderColor: v2CardBorder(theme),
        backgroundColor: v2CardBg(theme),
        color: "text.primary",
      }}
    >
      {!isStrategist && (
        <Autocomplete
          size="small"
          options={fundSelectOptions}
          value={selectedFund}
          onChange={(_event, next) => {
            handleFundChange(_event, next?.abbreviation ?? "ALL");
          }}
          getOptionLabel={(option) =>
            option?.name || option?.abbreviation || ""
          }
          isOptionEqualToValue={(option, value) =>
            fundAbbreviationsMatch(option?.abbreviation, value?.abbreviation)
          }
          filterOptions={filterFundOptions}
          disableClearable
          blurOnSelect
          sx={v2ControlFieldSx({ sm: 260, min: 220 })}
          renderOption={renderFundOption(theme)}
          renderInput={(params) => (
            <TextField
              {...params}
              label="Fund"
              placeholder="Search fund…"
              variant="outlined"
              size="small"
            />
          )}
          slotProps={v2AutocompleteSlotProps(theme)}
        />
      )}

      <Autocomplete
        size="small"
        disableClearable
        blurOnSelect
        value={StrategeyVal || ""}
        options={strategyOptions}
        onChange={handleStrategyChange}
        disabled={strategyDisabled}
        filterOptions={filterStringOptions}
        sx={v2ControlFieldSx({ sm: 240, min: 200 })}
        renderOption={renderTextOption}
        renderInput={(params) => (
          <TextField
            {...params}
            label="Strategy"
            placeholder="Search strategy…"
            variant="outlined"
            size="small"
          />
        )}
        slotProps={v2AutocompleteSlotProps(theme)}
      />

      <Autocomplete
        size="small"
        disableClearable
        blurOnSelect
        value={StrategeyTypename || "Both"}
        options={StrategyType}
        onChange={handleSideChange}
        disabled={strategyDisabled}
        sx={v2ControlFieldSx({ sm: 140, min: 120 })}
        renderOption={renderTextOption}
        renderInput={(params) => (
          <TextField
            {...params}
            label="Side"
            variant="outlined"
            size="small"
          />
        )}
        slotProps={v2AutocompleteSlotProps(theme)}
      />

      <PositionsCalculationControlsV2 />

      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          gap: 0.5,
          ml: { xs: 0, md: "auto" },
        }}
      >
        <Button
          size="medium"
          onClick={onTopMovers}
          aria-label="View top movers"
          startIcon={<TrendingUpOutlinedIcon sx={{ fontSize: 22 }} />}
          sx={{
            textTransform: "none",
            fontSize: "0.95rem",
            fontWeight: 600,
          }}
        >
          Top movers
        </Button>
        <Button
          size="medium"
          onClick={onExport}
          aria-label="Export portfolio"
          startIcon={<FileDownloadOutlinedIcon sx={{ fontSize: 22 }} />}
          sx={{
            textTransform: "none",
            fontSize: "0.95rem",
            fontWeight: 600,
          }}
        >
          Export
        </Button>
      </Box>
    </Box>
  );
};

export default ControlBar;
